/* ==========================================================================
   LONDON REPAIR OUTLET - COLLECTION FILTERS
   File: assets/lro-filters.js
   Enhances: sections/lro-filters.liquid, sections/lro-collection-toolbar.liquid
   Styles:   assets/lro-filters.css
   Data:     assets/lro-finder-index.json (assets/lro-nav.json as fallback),
             fetched lazily and shared with lro-finder.js through the same
             sessionStorage cache key.

   WHAT THIS FILE ADDS
   -------------------
   The filter system works completely without it: every option and chip is a
   real anchor to a tag-filter URL. This script adds, in order of value:

     1. The mobile bottom sheet. Below 64em the in-flow panel becomes a
        fixed sheet with a scrim, a focus trap and a body scroll lock.
        Option taps are STAGED locally so the drawer does not reload per
        tap; the Apply button carries the real URL at all times, and asks
        the Section Rendering API (the toolbar section, a few hundred
        bytes) for a live result count before you commit.
     2. The full model list. Liquid renders an A to Z sample because
        collection.all_tags caps at 1,000 entries; this script swaps in the
        complete per-brand model list from the finder index, ordered by
        stock depth, with a typeahead.
     3. Honest counts. A count is filled in ONLY when the precomputed index
        holds an exact figure for an option's whole target state (brand,
        brand x category, brand x model, brand x model x category,
        category, group, or a bare collection total). Anything else,
        including every grade combination, keeps its count hidden. No
        guesses, no estimates.
     4. Group collapse memory (localStorage), the sort dropdown navigating
        on change, and the grid density toggle.

   Chip removal and desktop option clicks are PLAIN NAVIGATION on purpose.
   Swapping Horizon's product grid in place would mean re-initialising its
   results-list and collection-component custom elements, whose re-init
   contract is not ours to rely on. A full navigation is cheap, correct and
   keeps the URL, the back button and analytics honest. The one place the
   Section Rendering API is used is the sheet's count preview, where it
   really is cheap.

   No dependencies, no globals beyond the load guard. Every entry point
   no-ops when its root element is absent.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__lroFiltersLoaded) return;
  window.__lroFiltersLoaded = true;

  /* ------------------------------------------------------------------------
     Small helpers
     ------------------------------------------------------------------------ */

  /** The rule Shopify uses to turn a tag into a URL segment. Identical to
   *  handleize() in lro-finder.js and js_handleize() in the build scripts. */
  function handleize(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /** Lower case, punctuation flattened to single spaces, for searching. */
  function normalise(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/^ +| +$/g, '');
  }

  var formatter = null;
  function fmt(n) {
    if (formatter === null) {
      try {
        formatter = new Intl.NumberFormat('en-GB');
      } catch (error) {
        formatter = { format: function (value) { return String(value); } };
      }
    }
    return formatter.format(n);
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var self = this;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  /* ------------------------------------------------------------------------
     The index. Mirrors the loader and decoders in lro-finder.js, including
     the cache key, so the two features share one fetch and one cached copy.
     ------------------------------------------------------------------------ */

  var inFlight = new Map();

  function readCache(key) {
    try {
      var cached = window.sessionStorage.getItem(key);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, text) {
    try {
      window.sessionStorage.setItem(key, text);
    } catch (error) {
      /* Private mode or full quota. The HTTP cache still has our back. */
    }
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error(url + ' responded ' + response.status);
      return response.text();
    });
  }

  function adaptIndex(raw) {
    return {
      partial: false,
      sep: raw.sep || '~',
      total: (raw.stats && raw.stats.products) || 0,
      groups: raw.groups || [],
      cats: (raw.cats || []).map(function (row) {
        return { name: row[0], handle: row[1], count: row[2], group: row[3] };
      }),
      brands: (raw.brands || []).map(function (brand) {
        return {
          name: brand.n,
          handle: brand.h,
          tag: brand.t || brand.n,
          count: brand.c,
          encodedCats: brand.k || '',
          encodedModels: brand.m || [],
          cats: null,
          models: null
        };
      })
    };
  }

  function adaptNav(raw) {
    var cats = (raw.types || []).map(function (type, i) {
      return { name: type.name, handle: type.handle, count: type.count, group: 0, order: i };
    });
    return {
      partial: true,
      sep: '~',
      total: (raw.stats && raw.stats.products) || 0,
      groups: ['All parts'],
      cats: cats,
      brands: (raw.brands || []).map(function (brand) {
        var rows = (raw.models_by_brand || {})[brand.name] || [];
        return {
          name: brand.name,
          handle: brand.handle,
          tag: brand.tag || brand.name,
          count: brand.count,
          encodedCats: '',
          encodedModels: [],
          cats: null,
          models: rows.map(function (model) {
            return {
              name: model.name,
              handle: handleize(model.name),
              count: model.count,
              cats: null,
              norm: normalise(model.name)
            };
          })
        };
      })
    };
  }

  function loadIndex(indexUrl, navUrl) {
    if (inFlight.has(indexUrl)) return inFlight.get(indexUrl);
    var cacheKey = 'lro-finder:' + indexUrl;
    var promise = new Promise(function (resolve, reject) {
      var cached = readCache(cacheKey);
      if (cached) {
        resolve(cached.v ? adaptIndex(cached) : adaptNav(cached));
        return;
      }
      fetchJson(indexUrl)
        .then(function (text) {
          writeCache(cacheKey, text);
          resolve(adaptIndex(JSON.parse(text)));
        })
        .catch(function () {
          if (!navUrl) { reject(new Error('no index')); return; }
          fetchJson(navUrl)
            .then(function (text) {
              writeCache(cacheKey, text);
              resolve(adaptNav(JSON.parse(text)));
            })
            .catch(reject);
        });
    });
    inFlight.set(indexUrl, promise);
    return promise;
  }

  /** "3:j,7:2,c" to [{ cat, count }]. Base 36 keys index into cats. */
  function decodeCounts(field, cats) {
    var out = { list: [], total: 0 };
    if (!field) return out;
    var tokens = field.split(',');
    for (var i = 0; i < tokens.length; i += 1) {
      var token = tokens[i];
      if (!token) continue;
      var colon = token.indexOf(':');
      var key = colon === -1 ? token : token.slice(0, colon);
      var count = colon === -1 ? 1 : parseInt(token.slice(colon + 1), 36);
      if (!isFinite(count)) continue;
      out.total += count;
      if (key === '*') continue; /* counted, but not filterable */
      var cat = cats[parseInt(key, 36)];
      if (cat) out.list.push({ cat: cat, count: count });
    }
    return out;
  }

  /** Front-coded model records to objects, once per brand. */
  function decodeModels(brand, index) {
    if (brand.models) return brand.models;
    var sep = index.sep;
    var cats = index.cats;
    var records = brand.encodedModels;
    var models = [];
    var previous = '';
    for (var i = 0; i < records.length; i += 1) {
      var record = records[i];
      var shared = parseInt(record.charAt(0), 36);
      if (!isFinite(shared)) continue;
      var rest = record.slice(1);
      var cut = rest.indexOf(sep);
      if (cut === -1) continue;
      var name = previous.slice(0, shared) + rest.slice(0, cut);
      previous = name;
      var tail = rest.slice(cut + 1).split(sep);
      var counts = decodeCounts(tail[0], cats);
      models.push({
        name: name,
        handle: tail.length > 1 && tail[1] ? tail[1] : handleize(name),
        count: counts.total,
        cats: counts.list,
        norm: normalise(name)
      });
    }
    brand.models = models;
    return models;
  }

  function decodeBrandCats(brand, index) {
    if (brand.cats) return brand.cats;
    brand.cats = decodeCounts(brand.encodedCats, index.cats).list;
    return brand.cats;
  }

  /* ------------------------------------------------------------------------
     Filters section
     ------------------------------------------------------------------------ */

  function initFilters(root) {
    if (root.__lroInit) return;
    root.__lroInit = true;
    root.classList.add('lro-js');

    var panel = root.querySelector('[data-lro-panel]');
    if (!panel) return;

    var scrim = root.querySelector('[data-lro-scrim]');
    var foot = root.querySelector('[data-lro-sheet-foot]');
    var applyLink = root.querySelector('[data-lro-apply]');
    var closeBtn = root.querySelector('[data-lro-close]');
    var modelsRoot = root.querySelector('[data-lro-models]');

    var qs = root.getAttribute('data-lro-qs') || '';
    var kind = root.getAttribute('data-lro-kind') || 'other';
    var collectionHandle = root.getAttribute('data-lro-collection') || '';
    var brandName = root.getAttribute('data-lro-brand-name') || '';
    var modelLimit = parseInt(root.getAttribute('data-lro-model-limit'), 10) || 12;

    var vocab = { brands: [], groups: [], types: [], grades: [] };
    try {
      vocab = JSON.parse(root.getAttribute('data-lro-vocab')) || vocab;
    } catch (error) {
      /* Malformed vocab leaves the plain-link behaviour intact. */
    }

    /* Lookup maps keyed by URL handle. */
    var brandByCollection = {}; /* samsung-parts -> Samsung */
    var brandNameHandles = {};  /* samsung -> Samsung */
    vocab.brands.forEach(function (row) {
      brandByCollection[row[1]] = row[0];
      brandNameHandles[handleize(row[0])] = row[0];
    });
    var groupByHandle = {};     /* display -> Display */
    vocab.groups.forEach(function (row) { groupByHandle[row[1]] = row[0]; });
    var typeByHandle = {};      /* screens -> { name, group } */
    vocab.types.forEach(function (row) {
      typeByHandle[row[1]] = { name: row[0], group: row[2] };
    });
    var gradeHandles = {};
    (vocab.grades || []).forEach(function (g) { gradeHandles[handleize(g)] = g; });

    function classify(tagHandle) {
      if (typeByHandle[tagHandle]) return 'type';
      if (groupByHandle[tagHandle]) return 'group';
      if (gradeHandles[tagHandle]) return 'grade';
      if (brandNameHandles[tagHandle]) return 'brandtag';
      /* Unknown tags are treated as device models. A handful of supplier
         names sit in the tag stream too; they simply never earn a count. */
      return 'model';
    }

    var currentTags = [];
    try {
      currentTags = JSON.parse(root.getAttribute('data-lro-current')) || [];
    } catch (error) { /* none */ }

    var pageState = {
      baseHandle: collectionHandle,
      tags: currentTags.map(handleize)
    };

    function cloneState(state) {
      return { baseHandle: state.baseHandle, tags: state.tags.slice() };
    }

    var staged = cloneState(pageState);
    var indexData = null;
    var brandEntry = null;      /* index entry for this brand collection */
    var modelBase = null;       /* { brand, model } when this is a model collection */
    var modelBaseSolved = false;
    var modelExpanded = false;
    var modelQuery = '';

    /* -------------------- URL state machine -------------------- */

    function buildUrl(state) {
      var url = '/collections/' + state.baseHandle;
      if (state.tags.length > 0) url += '/' + state.tags.join('+');
      return url + qs;
    }

    /** What the page state becomes if this option is activated. The same
     *  radio-per-group, AND-across-groups rules the Liquid uses. */
    function applyOption(state, el) {
      var group = el.getAttribute('data-lro-group');
      var switchTo = el.getAttribute('data-lro-switch');
      var handle = el.getAttribute('data-lro-handle');
      var next = cloneState(state);

      if (switchTo) {
        if (group === 'brand') {
          /* Models belong to the brand they came from: drop them. */
          next.tags = next.tags.filter(function (t) { return classify(t) !== 'model'; });
          /* Never carry a brand-name tag into a brand collection. */
          next.tags = next.tags.filter(function (t) { return classify(t) !== 'brandtag'; });
          if (state.baseHandle === switchTo) {
            next.baseHandle = 'all';
          } else {
            /* Leaving a category or group collection keeps its meaning as
               a tag on the brand collection. */
            var baseClass = typeByHandle[state.baseHandle] || groupByHandle[state.baseHandle];
            if (baseClass && next.tags.indexOf(state.baseHandle) === -1) {
              next.tags.push(state.baseHandle);
            }
            next.baseHandle = switchTo;
          }
        } else {
          /* Category switch: two categories ANDed match nothing, so the
             old category context goes. */
          next.tags = next.tags.filter(function (t) {
            var c = classify(t);
            return c !== 'type' && c !== 'group';
          });
          next.baseHandle = state.baseHandle === switchTo ? 'all' : switchTo;
        }
        return next;
      }

      var on = state.tags.indexOf(handle) !== -1;
      var strip = group === 'type' ? { type: 1, group: 1 }
        : group === 'grade' ? { grade: 1 }
          : { model: 1 };
      next.tags = next.tags.filter(function (t) { return !strip[classify(t)]; });
      if (!on) next.tags.push(handle);
      return next;
    }

    function isOn(state, el) {
      var switchTo = el.getAttribute('data-lro-switch');
      if (switchTo) return state.baseHandle === switchTo;
      return state.tags.indexOf(el.getAttribute('data-lro-handle')) !== -1;
    }

    /* -------------------- Honest counts -------------------- */

    function indexBrandByName(name) {
      if (!indexData) return null;
      for (var i = 0; i < indexData.brands.length; i += 1) {
        if (indexData.brands[i].name === name) return indexData.brands[i];
      }
      return null;
    }

    function catByHandle(handle) {
      if (!indexData) return null;
      for (var i = 0; i < indexData.cats.length; i += 1) {
        if (indexData.cats[i].handle === handle) return indexData.cats[i];
      }
      return null;
    }

    function catGroupName(cat) {
      return indexData.groups[cat.group];
    }

    function sumGroup(list, groupName) {
      var sum = 0;
      for (var i = 0; i < list.length; i += 1) {
        if (catGroupName(list[i].cat) === groupName) sum += list[i].count;
      }
      return sum;
    }

    function findModel(brand, handle) {
      var models = decodeModels(brand, indexData);
      for (var i = 0; i < models.length; i += 1) {
        if (models[i].handle === handle) return models[i];
      }
      return null;
    }

    /** This collection, if it is one of the 150 model collections, maps to
     *  a brand and model in the index. Solved once, lazily. */
    function solveModelBase() {
      if (modelBaseSolved) return modelBase;
      modelBaseSolved = true;
      modelBase = null;
      if (!indexData || indexData.partial) return null;
      if (!/-parts$/.test(collectionHandle)) return null;
      var wanted = collectionHandle.replace(/-parts$/, '');
      for (var i = 0; i < indexData.brands.length; i += 1) {
        var models = decodeModels(indexData.brands[i], indexData);
        for (var j = 0; j < models.length; j += 1) {
          if (models[j].handle === wanted) {
            modelBase = { brand: indexData.brands[i], model: models[j] };
            return modelBase;
          }
        }
      }
      return null;
    }

    /** Exact precomputed count for a whole target state, or null. Null
     *  keeps the count hidden, which is the honest default. */
    function resolveCount(target) {
      if (!indexData) return null;

      var brand = null;
      var cat = null;
      var grp = null;
      var model = null;

      /* The base collection. */
      if (target.baseHandle === 'all') {
        /* nothing to add */
      } else if (brandByCollection[target.baseHandle]) {
        brand = indexBrandByName(brandByCollection[target.baseHandle]);
        if (!brand) return null;
      } else if (typeByHandle[target.baseHandle]) {
        cat = catByHandle(target.baseHandle);
        if (!cat) return null;
      } else if (groupByHandle[target.baseHandle]) {
        grp = groupByHandle[target.baseHandle];
      } else if (kind === 'other' && target.baseHandle === collectionHandle) {
        var solved = solveModelBase();
        if (!solved) return null;
        brand = solved.brand;
        model = solved.model;
      } else {
        return null;
      }

      /* The tags. Every tag must land in a precomputed dimension. */
      for (var i = 0; i < target.tags.length; i += 1) {
        var t = target.tags[i];
        var c = classify(t);
        if (c === 'grade') return null;
        if (c === 'type') {
          if (cat || grp) return null;
          cat = catByHandle(t);
          if (!cat) return null;
        } else if (c === 'group') {
          if (cat || grp) return null;
          grp = groupByHandle[t];
        } else if (c === 'brandtag') {
          var named = indexBrandByName(brandNameHandles[t]);
          if (!named) return null;
          if (brand && brand !== named) return 0;
          brand = named;
        } else {
          /* model */
          if (!brand || indexData.partial) return null;
          if (model) return null;
          model = findModel(brand, t);
          if (!model) return null; /* below the index threshold: no claim */
        }
      }

      /* The combinations the data can actually answer. */
      if (model) {
        if (indexData.partial) return null;
        if (cat) {
          for (var j = 0; j < model.cats.length; j += 1) {
            if (model.cats[j].cat === cat) return model.cats[j].count;
          }
          return 0;
        }
        if (grp) return sumGroup(model.cats, grp);
        return model.count;
      }
      if (brand) {
        if (!cat && !grp) return brand.count;
        if (indexData.partial) return null;
        var brandCats = decodeBrandCats(brand, indexData);
        if (cat) {
          for (var k = 0; k < brandCats.length; k += 1) {
            if (brandCats[k].cat === cat) return brandCats[k].count;
          }
          return 0;
        }
        return sumGroup(brandCats, grp);
      }
      if (cat) return cat.count;
      if (grp) {
        if (indexData.partial) return null;
        var sum = 0;
        for (var m = 0; m < indexData.cats.length; m += 1) {
          if (catGroupName(indexData.cats[m]) === grp) sum += indexData.cats[m].count;
        }
        return sum;
      }
      if (target.baseHandle === 'all' && indexData.total > 0) return indexData.total;
      return null;
    }

    /* -------------------- Rendering -------------------- */

    var sheetMode = false;
    var mq = null;
    try {
      mq = window.matchMedia('(min-width: 64em)');
    } catch (error) { /* very old engine: desktop behaviour only */ }

    function activeState() {
      return sheetMode && root.hasAttribute('data-lro-open') ? staged : pageState;
    }

    function srText(on) {
      return on ? 'Applied. Activate to remove this filter.' : 'Activate to add this filter.';
    }

    function resyncOption(state, el) {
      var on = isOn(state, el);
      el.classList.toggle('is-on', on);
      el.setAttribute('data-lro-on', on ? 'true' : 'false');
      var sr = el.querySelector('.lro-visually-hidden');
      if (sr) sr.textContent = srText(on);
      var target = applyOption(state, el);
      if (sheetMode) el.setAttribute('href', buildUrl(target));
      var num = el.querySelector('[data-lro-num]');
      if (num) {
        var count = resolveCount(target);
        if (count === null) {
          num.hidden = true;
          num.textContent = '';
        } else {
          num.textContent = fmt(count);
          num.hidden = false;
        }
      }
    }

    function updateAppliedLabels(state) {
      var groups = panel.querySelectorAll('details[data-lro-facet]');
      groups.forEach(function (details) {
        var label = details.querySelector('[data-lro-applied-label]');
        if (!label) return;
        var on = details.querySelector('a[data-lro-opt].is-on .lro-facet__text');
        label.textContent = on ? on.textContent.trim() : '';
      });
    }

    var previewController = null;
    var fetchPreview = debounce(function (state) {
      if (!applyLink) return;
      var toolbar = document.querySelector('[data-lro-toolbar]');
      var sectionId = toolbar && toolbar.getAttribute('data-lro-section-id');
      if (!sectionId) return;
      var url = buildUrl(state);
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
      try {
        if (previewController) previewController.abort();
        previewController = new AbortController();
        fetch(url, { credentials: 'same-origin', signal: previewController.signal })
          .then(function (response) {
            if (!response.ok) throw new Error('preview ' + response.status);
            return response.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var counted = doc.querySelector('[data-lro-count]');
            if (!counted) return;
            var count = parseInt(counted.getAttribute('data-lro-count'), 10);
            if (!isFinite(count)) return;
            applyLink.textContent = 'Show ' + fmt(count) + ' ' + (count === 1 ? 'part' : 'parts');
          })
          .catch(function () { /* the plain label stands */ });
      } catch (error) { /* no fetch, no preview */ }
    }, 300);

    function updateApply(state) {
      if (!applyLink) return;
      applyLink.setAttribute('href', buildUrl(state));
      applyLink.textContent = 'Show parts';
      if (sheetMode && root.hasAttribute('data-lro-open')) fetchPreview(state);
    }

    function resync(state) {
      var options = panel.querySelectorAll('a[data-lro-opt]');
      options.forEach(function (el) { resyncOption(state, el); });
      if (brandEntry) renderModels(state);
      updateAppliedLabels(state);
      updateApply(state);
    }

    /* -------------------- The model list -------------------- */

    var modelList = modelsRoot && modelsRoot.querySelector('[data-lro-model-list]');
    var modelInput = modelsRoot && modelsRoot.querySelector('[data-lro-model-input]');
    var modelSearchWrap = modelsRoot && modelsRoot.querySelector('[data-lro-model-search]');
    var modelAllBtn = modelsRoot && modelsRoot.querySelector('[data-lro-model-all]');
    var modelStatus = modelsRoot && modelsRoot.querySelector('[data-lro-model-status]');

    function buildOption(state, model) {
      var li = document.createElement('li');
      li.className = 'lro-facet__item';
      var a = document.createElement('a');
      a.className = 'lro-facet__opt';
      a.setAttribute('data-lro-opt', '');
      a.setAttribute('data-lro-group', 'model');
      a.setAttribute('data-lro-tag', model.name);
      a.setAttribute('data-lro-handle', model.handle);
      a.setAttribute('rel', 'nofollow');

      var box = document.createElement('span');
      box.className = 'lro-facet__box';
      box.setAttribute('aria-hidden', 'true');
      box.innerHTML = '<svg class="lro-facet__tick" viewBox="0 0 12 12" focusable="false"><path d="M1.5 6.2 4.4 9l6-6.4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      a.appendChild(box);

      var text = document.createElement('span');
      text.className = 'lro-facet__text';
      text.textContent = model.name;
      a.appendChild(text);

      var num = document.createElement('span');
      num.className = 'lro-facet__num lro-tabular';
      num.setAttribute('data-lro-num', '');
      num.hidden = true;
      a.appendChild(num);

      var sr = document.createElement('span');
      sr.className = 'lro-visually-hidden';
      a.appendChild(sr);

      li.appendChild(a);
      resyncOption(state, a);
      /* Dynamic rows have no server href, so both modes get the built one. */
      a.setAttribute('href', buildUrl(applyOption(state, a)));
      return li;
    }

    function renderModels(state) {
      if (!brandEntry || !modelList) return;
      var models = decodeModels(brandEntry, indexData);
      var shown = models;

      if (modelQuery) {
        var q = normalise(modelQuery);
        shown = models.filter(function (m) { return m.norm.indexOf(q) !== -1; });
      } else if (!modelExpanded) {
        shown = models.slice(0, modelLimit);
        /* The applied model always stays visible. */
        var appliedHandle = null;
        for (var i = 0; i < state.tags.length; i += 1) {
          if (classify(state.tags[i]) === 'model') { appliedHandle = state.tags[i]; break; }
        }
        if (appliedHandle) {
          var present = shown.some(function (m) { return m.handle === appliedHandle; });
          if (!present) {
            var applied = findModel(brandEntry, appliedHandle);
            if (applied) shown = [applied].concat(shown);
          }
        }
      }

      var fragment = document.createDocumentFragment();
      shown.forEach(function (m) { fragment.appendChild(buildOption(state, m)); });
      modelList.replaceChildren(fragment);

      if (modelStatus) {
        modelStatus.textContent = fmt(shown.length) + ' of ' + fmt(models.length) + ' models shown';
      }
      if (modelAllBtn) {
        var more = !modelQuery && !modelExpanded && models.length > shown.length;
        modelAllBtn.hidden = !more;
        if (more) modelAllBtn.textContent = 'Show all ' + fmt(models.length) + ' models';
      }
    }

    if (modelInput) {
      modelInput.addEventListener('input', debounce(function () {
        modelQuery = modelInput.value.trim();
        renderModels(activeState());
      }, 120));
    }
    if (modelAllBtn) {
      modelAllBtn.addEventListener('click', function () {
        modelExpanded = true;
        renderModels(activeState());
      });
    }

    /* -------------------- Index loading, lazily -------------------- */

    var indexRequested = false;
    function wantIndex() {
      if (indexRequested) return;
      indexRequested = true;
      var indexUrl = root.getAttribute('data-lro-index-url');
      var navUrl = root.getAttribute('data-lro-nav-url');
      if (!indexUrl) return;
      loadIndex(indexUrl, navUrl).then(function (data) {
        indexData = data;
        if (kind === 'brand' && brandName) {
          brandEntry = indexBrandByName(brandName);
          if (brandEntry && !indexData.partial) decodeModels(brandEntry, indexData);
          if (brandEntry && modelSearchWrap) modelSearchWrap.hidden = false;
        }
        resync(activeState());
      }).catch(function () { /* plain links, no counts: still fully honest */ });
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.disconnect();
            wantIndex();
          }
        });
      }, { rootMargin: '400px' });
      observer.observe(root);
    } else {
      wantIndex();
    }
    panel.addEventListener('focusin', wantIndex, { once: true });

    /* -------------------- Group collapse memory -------------------- */

    var MEMORY_KEY = 'lro:facets:v1';
    function readMemory() {
      try {
        return JSON.parse(window.localStorage.getItem(MEMORY_KEY)) || {};
      } catch (error) {
        return {};
      }
    }

    var memory = readMemory();
    panel.querySelectorAll('details[data-lro-facet]').forEach(function (details) {
      var key = details.getAttribute('data-lro-facet');
      if (Object.prototype.hasOwnProperty.call(memory, key)) {
        details.open = memory[key] === 1;
      }
      details.addEventListener('toggle', function () {
        wantIndex();
        var state = readMemory();
        state[key] = details.open ? 1 : 0;
        try {
          window.localStorage.setItem(MEMORY_KEY, JSON.stringify(state));
        } catch (error) { /* private mode */ }
      });
    });

    /* -------------------- Sheet mechanics -------------------- */

    var lastFocus = null;
    var scrimTimer = null;

    function lockScroll() {
      var gap = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.classList.add('lro-scroll-lock');
      if (gap > 0) document.documentElement.style.paddingInlineEnd = gap + 'px';
    }

    function unlockScroll() {
      document.documentElement.classList.remove('lro-scroll-lock');
      document.documentElement.style.paddingInlineEnd = '';
    }

    function focusables() {
      var nodes = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      );
      return Array.prototype.filter.call(nodes, function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSheet();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = focusables();
      if (items.length === 0) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openSheet(trigger) {
      if (!sheetMode || root.hasAttribute('data-lro-open')) return;
      lastFocus = trigger || document.activeElement;
      staged = cloneState(pageState);
      root.setAttribute('data-lro-open', '');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-labelledby', 'lro-filters-title');
      if (scrim) {
        window.clearTimeout(scrimTimer);
        scrim.hidden = false;
        window.requestAnimationFrame(function () { scrim.classList.add('is-open'); });
      }
      lockScroll();
      resync(staged);
      panel.focus();
      document.addEventListener('keydown', onKeydown, true);
      setTriggerState(true);
    }

    function closeSheet() {
      if (!root.hasAttribute('data-lro-open')) return;
      root.removeAttribute('data-lro-open');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('aria-labelledby');
      if (scrim) {
        scrim.classList.remove('is-open');
        scrimTimer = window.setTimeout(function () { scrim.hidden = true; }, 350);
      }
      unlockScroll();
      document.removeEventListener('keydown', onKeydown, true);
      staged = cloneState(pageState);
      resync(pageState);
      setTriggerState(false);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function setTriggerState(open) {
      document.querySelectorAll('[data-lro-open-filters]').forEach(function (el) {
        el.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    function setMode() {
      sheetMode = mq ? !mq.matches : false;
      if (sheetMode) {
        root.setAttribute('data-lro-mode', 'sheet');
        if (foot) foot.hidden = false;
        if (closeBtn) closeBtn.hidden = false;
        document.querySelectorAll('[data-lro-open-filters]').forEach(function (el) {
          el.setAttribute('aria-haspopup', 'dialog');
          el.setAttribute('aria-expanded', 'false');
        });
      } else {
        closeSheet();
        root.removeAttribute('data-lro-mode');
        if (foot) foot.hidden = true;
        if (closeBtn) closeBtn.hidden = true;
        document.querySelectorAll('[data-lro-open-filters]').forEach(function (el) {
          el.removeAttribute('aria-haspopup');
          el.removeAttribute('aria-expanded');
        });
      }
      resync(activeState());
    }

    if (mq) {
      try {
        mq.addEventListener('change', setMode);
      } catch (error) {
        mq.addListener(setMode);
      }
    }
    setMode();

    if (closeBtn) closeBtn.addEventListener('click', closeSheet);
    if (scrim) scrim.addEventListener('click', closeSheet);

    /* Option taps inside the open sheet are staged, not navigated. The
       Apply link always carries the real URL, so a long press or a middle
       click still gets a working address. */
    panel.addEventListener('click', function (event) {
      var opt = event.target.closest('a[data-lro-opt]');
      if (!opt) return;
      if (!sheetMode || !root.hasAttribute('data-lro-open')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      staged = applyOption(staged, opt);
      resync(staged);
    });

    root.__lroApi = {
      open: openSheet,
      isSheet: function () { return sheetMode; }
    };
  }

  /* Filter triggers can live in other sections (the toolbar), so the
     listener is document level and finds the panel at click time. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-lro-open-filters]');
    if (!trigger) return;
    var root = document.querySelector('[data-lro-filters]');
    if (!root || !root.__lroApi) return;
    if (root.__lroApi.isSheet()) {
      event.preventDefault();
      root.__lroApi.open(trigger);
    }
    /* Desktop: the anchor scrolls to the in-flow panel, which is right. */
  });

  /* ------------------------------------------------------------------------
     Toolbar
     ------------------------------------------------------------------------ */

  var DENSITY_KEY = 'lro:density';

  function initToolbar(root) {
    if (root.__lroInit) return;
    root.__lroInit = true;
    root.classList.add('lro-js');

    /* Sort: navigate on change, Go button hidden by the lro-js class. */
    var form = root.querySelector('[data-lro-sort-form]');
    var select = root.querySelector('[data-lro-sort]');
    if (form && select) {
      select.addEventListener('change', function () {
        var action = form.getAttribute('action') || window.location.pathname;
        window.location.assign(action + '?sort_by=' + encodeURIComponent(select.value));
      });
    }

    /* Density: a script preference, revealed by the script. */
    var density = root.querySelector('[data-lro-density]');
    if (density) {
      density.hidden = false;
      var buttons = density.querySelectorAll('[data-lro-density-btn]');

      function paint() {
        var compact = document.documentElement.classList.contains('lro-density-compact');
        buttons.forEach(function (button) {
          var mine = button.getAttribute('data-lro-density-btn') === (compact ? 'compact' : 'cosy');
          button.setAttribute('aria-pressed', mine ? 'true' : 'false');
        });
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          var compact = button.getAttribute('data-lro-density-btn') === 'compact';
          document.documentElement.classList.toggle('lro-density-compact', compact);
          try {
            window.localStorage.setItem(DENSITY_KEY, compact ? 'compact' : 'cosy');
          } catch (error) { /* private mode */ }
          paint();
        });
      });

      paint();
    }
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function boot(scope) {
    var where = scope && scope.querySelectorAll ? scope : document;
    where.querySelectorAll('[data-lro-filters]').forEach(initFilters);
    where.querySelectorAll('[data-lro-toolbar]').forEach(initToolbar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }

  /* Theme editor: sections reload in place. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
